import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ItineraryauthService } from './index'
import  { environment } from '../../environments/environment'

export interface FlightSearchInterface {
  name: string
  city: string
  country: string
  iata: string
}

export interface AirportGroup {
  label: string;   // e.g. "Nairobi, Kenya"
  city: string;
  country: string;
  primary: FlightSearchInterface;
  others: FlightSearchInterface[];
}

@Injectable({
  providedIn: 'root'
})
export class ItineraryServiceService {

  public apiUrl  = environment.apiUrl
  public SearchURL = environment.getCities;
  private airportsUrl = 'assets/airports.json'; // Path to local JSON file

  constructor(private http: HttpClient, private auth:ItineraryauthService) {}

  getFlights(data:any): Observable<FlightSearchInterface[]>{
    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }
        console.log(token)
        let headers = new HttpHeaders()
        headers = headers.append('Authorization', `Bearer ${token}`)
        return this.http
          .get(this.SearchURL, { params: params, headers:headers});
      })
    )
  }

   getAirports(): Observable<FlightSearchInterface[]> {
    return this.http.get<{[key: string]: FlightSearchInterface}>(this.airportsUrl).pipe(
      map(response => this.convertObjectToArray(response)),
      catchError(error => {
        console.error('Error fetching airport data:', error);
        return of([]);
      })
    );
  }

  private convertObjectToArray(airports: {[key: string]: FlightSearchInterface}): FlightSearchInterface[] {
    return Object.keys(airports).map(key => airports[key]);
  }

  /**
   * Look up a single airport by its IATA code from the local airports.json.
   * Used to rebuild full airport objects (name/city/country) from a shared URL
   * that only carries IATA codes. Resolves to undefined if not found.
   */
  public getAirportByIata(iata: string): Observable<FlightSearchInterface | undefined> {
    const code = (iata || '').toUpperCase();
    return this.getAirports().pipe(
      map(airports => airports.find(a => (a.iata || '').toUpperCase() === code))
    );
  }

  public searchAirports(query: string): Observable<FlightSearchInterface[]> {
    const q = query.toLowerCase();
    return this.getAirports().pipe(
      map(airports => {
        const matched = airports.filter(airport =>
          airport.iata.toLowerCase().includes(q) ||
          airport.city.toLowerCase().includes(q) ||
          airport.name.toLowerCase().includes(q) ||
          airport.country.toLowerCase().includes(q)
        );
        // Exact IATA match floats to the top
        matched.sort((a, b) => {
          const aExact = a.iata.toLowerCase() === q ? -1 : 0;
          const bExact = b.iata.toLowerCase() === q ? 1 : 0;
          return aExact + bExact;
        });
        return matched;
      })
    );
  }

  /**
   * Group flat airport results by city + country so the dropdown can present a
   * primary airport per city with any other airports in the same city nested
   * beneath it. Group order follows the incoming relevance order; within a
   * group the main international airport floats to the top.
   */
  public groupAirports(airports: FlightSearchInterface[]): AirportGroup[] {
    const order: string[] = [];
    const groups = new Map<string, FlightSearchInterface[]>();

    for (const airport of airports || []) {
      const key = `${(airport.city || '').toLowerCase()}|${(airport.country || '').toLowerCase()}`;
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)!.push(airport);
    }

    return order.map(key => {
      const list = [...groups.get(key)!].sort((a, b) => this.airportRank(a) - this.airportRank(b));
      const [primary, ...others] = list;
      return {
        label: [primary.city, primary.country].filter(Boolean).join(', '),
        city: primary.city,
        country: primary.country,
        primary,
        others
      };
    });
  }

  private airportRank(airport: FlightSearchInterface): number {
    return /international|intl/i.test(airport.name || '') ? 0 : 1;
  }

  getFlightSchedule( departure:string, destination:string, data:any) {

    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }
        let headers = new HttpHeaders()
        headers = headers.append('Authorization', `Bearer ${token}`)
        return this.http
          .get(`${this.apiUrl}schedules/flights/${departure}/${destination}/`, {params: params, headers: headers})
      })
    );



  }


  holdFlightBooking(data:any) {
    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }
        let headers = new HttpHeaders()
        headers = headers.append('Authorization', `Bearer ${token}`)

        return this.http.post(`${this.apiUrl}booking/hold/flights/`,data,{
          headers: new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization','Bearer '+token)
        });
       
      })
    );
  }

  requestPayment(data: any) {
    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        return this.http.post(`${this.apiUrl}payments/request`, data, {
          headers: new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization', 'Bearer ' + token)
        });
      })
    );
  }

  checkFlightBooking(data:any) {
    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }
        let headers = new HttpHeaders()
        headers = headers.append('Authorization', `Bearer ${token}`)

        return this.http.post(`${this.apiUrl}booking/check/`,data,{
          headers: new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization','Bearer '+token)
        });
       
      })
    );
  }

  initiateStk(data:any) {
    return this.auth.getToken().pipe(
      switchMap((token): Observable<any> => {
        let params = new HttpParams();
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            params = params.set(key, data[key]);
          }
        }
        let headers = new HttpHeaders()
        headers = headers.append('Authorization', `Bearer ${token}`)

        return this.http.post(`${this.apiUrl}booking/confirm/flights/`,data,{
          headers: new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization','Bearer '+token)
        });
       
      })
    );
  }


getBookings(internal_id:any, booking_id:any) {
  return this.auth.getToken().pipe(
    switchMap((token): Observable<any> => {
    
      let headers = new HttpHeaders()
      headers = headers.append('Authorization', `Bearer ${token}`)
    

      return this.http.get(`${this.apiUrl}bookings?internal_booking_reference=` + internal_id,{
        headers: new HttpHeaders()
          .set('Content-Type', 'application/json')
          .set('Authorization','Bearer '+token)
      });
     
    })
  );
}


getBookingsCard(internal_id:any) {
  return this.auth.getToken().pipe(
    switchMap((token): Observable<any> => {
    
      let headers = new HttpHeaders()
      headers = headers.append('Authorization', `Bearer ${token}`)
    

      return this.http.get(`${this.apiUrl}bookings?internal_booking_reference=` + internal_id,{
        headers: new HttpHeaders()
          .set('Content-Type', 'application/json')
          .set('Authorization','Bearer '+token)
      });
     
    })
  );
}



}
